import { createContext, useContext } from 'react';
import type { Product, RoutineSet } from './types';

// What the shared product page needs from the site it runs on. rxk and
// Pick.K Seoul each wrap the page in <ProductHostContext.Provider> with
// their own links, catalog, cart step, account and wishlist. Everything in
// src/shared/rxk-product is otherwise identical in both repos, so a design
// change made in rxk is copied to Pick.K Seoul as-is.

export interface ReviewInput {
  sku: string;
  customerId: string;
  customerName: string;
  skinType: string;
  rating: number;
  reviewText: string;
}

export interface ProductHost {
  links: {
    home: string;
    homeLabel?: string;
    products: string;
    // A routine product's page, or null when this site does not sell it.
    product: (p: Product) => string | null;
    // The 3-Step Set's page, or null when this site does not sell it.
    set: string | null;
    login: string;
  };
  products: Product[];
  routineSet: RoutineSet | null;
  toast: (message: string) => void;
  currentUser: { id: string; name: string } | null;
  // Staff preview of the new design (shows sample reviews when none are live).
  isPreview: boolean;
  // null hides the wishlist heart.
  wishlist: { has: (id: string) => boolean; toggle: (id: string) => boolean } | null;
  // null hides "Write a review".
  submitReview: ((input: ReviewInput) => Promise<void>) | null;
  // After the item is in the cart: go to checkout (or this site's order step).
  checkout: () => void;
  // Sections this site hides (SECTION_KEYS). rxk shows them all.
  hidden?: string[];
  // A line at the foot of the page, e.g. who wrote customised text.
  credit?: string | null;
}

// The sections of the product page that a host may hide, in page order.
export const SECTION_KEYS = ['marquee', 'why', 'beforeAfter', 'skins', 'ingredients', 'ritual', 'watch', 'texture', 'routine', 'reviews', 'faq', 'trust'] as const;
export type SectionKey = (typeof SECTION_KEYS)[number];

export const ProductHostContext = createContext<ProductHost | null>(null);

export function useProductHost(): ProductHost {
  const host = useContext(ProductHostContext);
  if (!host) throw new Error('The shared product page needs a ProductHostContext provider.');
  return host;
}
